import { mapActions } from 'vuex';
import { CHATBOT_COMMANDS } from '~/utils/chat-flags';
import { getPhoneCreditPrepaidTransaction } from '~/api/phone-credit-prepaid';
import { getInvoices } from '~/api/invoices';
import { fetchNeoToggles } from '~/api/neo';

export default {
  methods: {
    ...mapActions({
      fetchNeoConfigs: 'neo/fetchNeoConfigs',
    }),
    /**
 * Checks phone credit prepaid payment duration
 * @command - '[apicall] checkPaidPhoneCreditDuration [/apicall]'
 * @param {object} currentTrx - currently selected transaction object
 */
    async checkPaidPhoneCreditDuration(currentTrx) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      const responses = {
        paidPhoneCreditLessThan15minutes: '[apiresponse] paidPhoneCreditLessThan15minutes [/apiresponse]',
        paidPhoneCreditMoreThan15minutes: '[apiresponse] paidPhoneCreditMoreThan15minutes [/apiresponse]',
        paidPhoneCreditError: '[apiresponse] paidPhoneCreditError [/apiresponse]',
      };
      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;
        const {
          data: [config],
        } = await this.fetchNeoConfigs(['csm-auto-refund-eligibility']);
        const { client_wait_time: clientWaitTime } = config.data || {};

        // Get phone-credit-prepaid transaction data
        const { data: phoneCreditPrepaidData } = await getPhoneCreditPrepaidTransaction(this.$axios, invoiceTrx?.id);

        // Get paid at time with current time difference
        const nowTime = new Date().getTime();
        const paidAtTime = new Date(phoneCreditPrepaidData?.state_changed_at?.paid_at).getTime();
        const paidTimeDifferenceInMinute = Math.floor((nowTime - paidAtTime) / 1000 / 60);
        const waitTime = clientWaitTime || 15;

        if (paidTimeDifferenceInMinute < waitTime) {
          this.$zopim.sendChatMsg(responses.paidPhoneCreditLessThan15minutes);
          return;
        }
        this.$zopim.sendChatMsg(responses.paidPhoneCreditMoreThan15minutes);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.paidPhoneCreditError);
        console.error('[ERROR] Something is wrong when trying to check phone prepaid transaction', error);
      }
    },

    /**
     * Show Credit Serial Number
     * @command - '[apicall] showPhoneCreditSerialNumber [/apicall]'
     * @param {object} currentTrx - currently selected transaction object
     */
    async showPhoneCreditSerialNumber(currentTrx) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      const responses = {
        serialNumberExists: '[apiresponse] serialNumberExists [/apiresponse]',
        specialMessage: '[special-message] { "type": "phone-serial-number", "text": "<serial_number>" }',
        serialNumberNotExists: '[apiresponse] serialNumberNotExists [/apiresponse]',
        serialNumberError: '[apiresponse] serialNumberError [/apiresponse]',
        trxStateIsReadyAutorefundForSecondaryFailed: '[apiresponse] trxStateIsReadyAutorefundForSecondaryFailed [/apiresponse]'
      };
      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        // Get phone-credit-prepaid transaction data
        const { data: phoneCreditPrepaidData } = await getPhoneCreditPrepaidTransaction(this.$axios, invoiceTrx?.id);

        if (phoneCreditPrepaidData?.serial_number) {
          const finalResponse = responses.specialMessage.replace(
            '<serial_number>',
            phoneCreditPrepaidData?.serial_number
          );
          this.sendMessage({
            msg: finalResponse,
            nick: 'agent:trigger', // impersonate Acha Bot
            displayName: 'Acha',
          });
          this.$zopim.sendChatMsg(responses.serialNumberExists);
          return;
        }

        const { data = [] } = await fetchNeoToggles(['o2o/autorefund-for-secondary-failed']);
        const isActive = data.find(element => element.id === 'o2o/autorefund-for-secondary-failed')?.active || false;

        if (isActive) {
          this.$zopim.sendChatMsg(responses.trxStateIsReadyAutorefundForSecondaryFailed);
          return;
        }

        this.$zopim.sendChatMsg(responses.serialNumberNotExists);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.serialNumberError);
        console.error('[ERROR] Something is wrong when trying to check phone prepaid transaction', error);
      }
    },
  },
};
